import { AmountValidator } from '@src/common/fields/AmountValidator';
import { DateField } from '@src/permitField/PermitFields';
import type { FormConditionSheet } from '@src/permits/completePermit/fields/ConditionSheets';
import { ConditionSheetsField } from '@src/permits/fields/Fields';
import * as yup from 'yup';

export enum CompleteAction {
  DELIVER = 'DELIVER',
  FLAG_ERROR = 'FLAG_ERROR',
  DENY = 'DENY',
}

export const isNoteRequired = (
  selectedAction: CompleteAction | '',
): boolean => {
  return (
    selectedAction === CompleteAction.DENY ||
    selectedAction === CompleteAction.FLAG_ERROR
  );
};

export class CompletionTypeField {
  public static fieldName = 'completionType' as const;
  public static options: CompleteAction[] = Object.values(CompleteAction);
  public static validator = yup
    .string()
    .oneOf(CompletionTypeField.options)
    .required();
}

export class JurisdictionFeeField {
  public static fieldName = 'jurisdictionFee' as const;
  public static validator = AmountValidator;
}

export class ServiceFeeField {
  public static fieldName = 'serviceFee' as const;
  public static validator = AmountValidator;
}

export class EffectiveDateField extends DateField {
  public static fieldName = 'effectiveDate' as const;
}

export class ExpirationDateField {
  public static fieldName = 'expirationDate' as const;
  public static validator = yup
    .date()
    .transform((curr, orig) => (orig === '' ? undefined : curr))
    .when(
      [
        '$isSelfIssued',
        '$validateExpirationDate',
        '$validateExpirationTime',
        CompletionTypeField.fieldName,
      ],
      {
        is: (
          isSelfIssued: boolean,
          validateExpirationDate: boolean,
          validateExpirationTime: boolean,
          completionType: CompleteAction,
        ) =>
          (isSelfIssued || completionType === CompleteAction.DELIVER) &&
          (validateExpirationDate || validateExpirationTime),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.notRequired(),
      },
    );
}

export class StatePermitNumberField {
  public static fieldName = 'statePermitNumber' as const;
  public static validator = yup
    .string()
    .trim()
    .when(['$isSelfIssued', CompletionTypeField.fieldName], {
      is: (isSelfIssued: boolean, completionType: CompleteAction) =>
        isSelfIssued || completionType === CompleteAction.DELIVER,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired(),
    });
}

export class StatePermitDocumentFields {
  public static fieldName = 'statePermitDocument' as const;
  public static validator = yup
    .object()
    .when(CompletionTypeField.fieldName, {
      is: (action: CompleteAction) => action === CompleteAction.FLAG_ERROR,
      then: (schema) =>
        schema
          .shape({
            name: yup.string().ensure().required(),
            documentId: yup.string().ensure().required(),
            sendToClient: yup.boolean().notRequired(),
          })
          .required(),
      otherwise: (schema) =>
        schema
          .shape({
            name: yup.string().ensure().notRequired(),
            documentId: yup.string().ensure().notRequired(),
            sendToClient: yup.boolean().notRequired(),
          })
          .optional(),
    })
    .optional();
}

export class DocumentsFields {
  public static fieldName = 'documents' as const;
  public static validator = yup.array().of(
    yup.object().shape({
      name: yup.string().ensure().required(),
      displayName: yup.string().optional(),
      documentId: yup.string().ensure().required(),
      sendToClient: yup.boolean().required(),
    }),
  );
}

type FeesFields = {
  [JurisdictionFeeField.fieldName]?: number;
  [ServiceFeeField.fieldName]?: number;
};
export type ErrorPermitFields = {
  [EffectiveDateField.fieldName]: Date;
  [ExpirationDateField.fieldName]?: Date;
  [StatePermitNumberField.fieldName]?: string;
  [StatePermitDocumentFields.fieldName]?: {
    name?: string;
    documentId?: string;
    sendToClient?: boolean;
  };
  [DocumentsFields.fieldName]?: {
    name: string;
    displayName?: string;
    documentId: string;
    sendToClient: boolean;
    showToClient?: boolean;
  }[];
  [CompletionTypeField.fieldName]:
    | CompleteAction.DENY
    | CompleteAction.FLAG_ERROR;
  [ConditionSheetsField.fieldName]?: {
    id: string;
    filePath: string;
    sendToClient: boolean;
  }[];
} & FeesFields;

export type DeliverPermitFields = {
  [EffectiveDateField.fieldName]: Date;
  [ExpirationDateField.fieldName]: Date;
  [StatePermitNumberField.fieldName]: string;
  [CompletionTypeField.fieldName]: CompleteAction.DELIVER;
  [ConditionSheetsField.fieldName]?: FormConditionSheet[];
} & FeesFields;

export type CompletePermitFields = DeliverPermitFields | ErrorPermitFields;

export const CompletePermitSchema = yup
  .object()
  .shape({
    [JurisdictionFeeField.fieldName]: JurisdictionFeeField.validator,
    [ServiceFeeField.fieldName]: ServiceFeeField.validator,
    [EffectiveDateField.fieldName]: EffectiveDateField.validator,
    [ExpirationDateField.fieldName]: ExpirationDateField.validator,
    [StatePermitNumberField.fieldName]: StatePermitNumberField.validator,
    [CompletionTypeField.fieldName]: CompletionTypeField.validator,
    [StatePermitDocumentFields.fieldName]: StatePermitDocumentFields.validator,
    [DocumentsFields.fieldName]: DocumentsFields.validator,
    [ConditionSheetsField.fieldName]: ConditionSheetsField.validator,
  })
  .required();

export type CompletePermitSchemaType = yup.InferType<
  typeof CompletePermitSchema
>;
